import { useAssignmentReferenceBookingsModalContext } from "@/modals/partners/assignment-reference-bookings-modal/context";
import { moment } from "@msuite/utils/date";
import type {
	AssignmentReferenceBookingsSchema,
	AssignmentReferenceSingleBookingAdjustmentSchema,
	AssignmentReferenceSingleBookingSchema,
} from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import { useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

/** The answers the host gives a booking; a booking carrying none of them is still open. */
const bookingAnswers = ["accepted", "rejected", "adjusted"];

/**
 * Whether the host has answered a booking — accepted it, rejected it or corrected it. A booking
 * whose answer is still open in its inputs has no result, so it counts as unanswered.
 *
 * @param result - The result the booking's adjustment carries.
 * @returns `true` once the booking has one of the three answers.
 */
export function isBookingAnswered(result: Nullable<string>) {
	return !!result && bookingAnswers.includes(result);
}

type StatedHours = Pick<AssignmentReferenceSingleBookingSchema, "checkIn" | "checkOut" | "pause">;

/**
 * Whether the partner has moved a booking's hours away from those it was loaded with. The times
 * are compared as instants, because the time input writes them in another format than the server
 * sends.
 */
function hasMovedHours(loaded: Partial<StatedHours>, stated: Partial<StatedHours>) {
	return (
		!moment(loaded.checkIn).isSame(stated.checkIn) ||
		!moment(loaded.checkOut).isSame(stated.checkOut) ||
		loaded.pause !== stated.pause
	);
}

/**
 * The host's answer to one booking, as the native review gives it: accept the booking as
 * submitted, reject it, or correct it. Accepting and rejecting copy the submitted hours into the
 * answer — the server derives the verdict from the two side by side and reads a rejection off the
 * result. Correcting opens the answer's inputs until the host takes the correction or drops it,
 * and dropping it puts the answer back as it was.
 *
 * A partner who moves the hours of an answered booking takes the answer back with the save — the
 * database withdraws it — so for the partner the booking reads unanswered from the moment its
 * hours move.
 *
 * @param index - The booking's position in the form.
 * @returns The booking's result, whether its inputs are open, and the five ways to change both.
 */
export function useBookingAnswer(index: number) {
	/** Context */
	const methods = useFormContext<AssignmentReferenceBookingsSchema>();
	const { role, hostCreatedBookingIds } = useAssignmentReferenceBookingsModalContext();

	/** State */
	// A row the host added has nothing submitted to answer, so it opens on its inputs.
	const [isEditing, setIsEditing] = useState(
		() => role === "host" && hostCreatedBookingIds.has(methods.getValues(`bookings.${index}.id`)),
	);
	const snapshot = useRef<Nullable<AssignmentReferenceSingleBookingAdjustmentSchema>>(null);
	const storedResult = useWatch({
		control: methods.control,
		name: `bookings.${index}.adjustment.result`,
	});
	const [checkIn, checkOut, pause] = useWatch({
		control: methods.control,
		name: [`bookings.${index}.checkIn`, `bookings.${index}.checkOut`, `bookings.${index}.pause`],
	});

	/** Functions */
	function setAdjustment(adjustment: AssignmentReferenceSingleBookingAdjustmentSchema) {
		methods.setValue(`bookings.${index}.adjustment`, adjustment, { shouldDirty: true });
	}

	function answerAsSubmitted(answer: "accepted" | "rejected") {
		const booking = methods.getValues(`bookings.${index}`);
		if (!booking.adjustment) return;
		setAdjustment({
			...booking.adjustment,
			checkIn: booking.checkIn,
			checkOut: booking.checkOut,
			pause: booking.pause,
			result: answer,
		});
	}

	function startEditing() {
		const adjustment = methods.getValues(`bookings.${index}.adjustment`);
		snapshot.current = adjustment;
		if (adjustment) setAdjustment({ ...adjustment, result: null });
		setIsEditing(true);
	}

	function cancelEditing() {
		if (snapshot.current) setAdjustment(snapshot.current);
		setIsEditing(false);
	}

	function confirmEditing() {
		methods.setValue(`bookings.${index}.adjustment.result`, "adjusted", { shouldDirty: true });
		setIsEditing(false);
	}

	/** Computed */
	const bookingId = methods.getValues(`bookings.${index}.id`);
	const loaded = methods.formState.defaultValues?.bookings?.find(
		(booking) => booking?.id === bookingId,
	);
	const isRestated =
		role === "partner" && !!loaded && hasMovedHours(loaded, { checkIn, checkOut, pause });
	const result = isRestated ? null : storedResult;

	/** Return */
	return {
		result,
		isEditing,
		accept: () => answerAsSubmitted("accepted"),
		reject: () => answerAsSubmitted("rejected"),
		startEditing,
		cancelEditing,
		confirmEditing,
	};
}
